// webapp/src/lib/sentryRateLimit.ts
//
// LocalStorage-backed rate limit for the Sentry beforeSend hook.
// 100 events per rolling 1h window. Phase 18 LAUNCH-14.
//
// LIMITATION (cross-tab) :
//   LocalStorage rate-limit is best-effort. Cross-tab burst possible
//   (typical 100 + N_tabs/hour). `shouldCaptureEvent` (read) and
//   `recordCapture` (read-modify-write) are separate operations: two tabs
//   reading count=99 simultaneously will both decide to capture and both
//   write count=100. Effective cap = 100 + N_tabs in worst case.
//   Acceptable trade-off for the 5K/month Sentry Free quota — typical user
//   has 1-3 tabs, so worst-case 103/hour stays well within budget.
//   Single-tab cap is deterministic and tested.
//   (Gemini Gate 1 2026-05-07 — chose Option A over BroadcastChannel
//   coordination for MVP simplicity; revisit if multi-tab burst observed
//   in production.)
//
// CLOCK-SKEW GUARD (Gemini Gate 1 2026-05-07) :
//   If user system time moves backward (timezone change, NTP correction,
//   manual clock change), `Date.now() < windowStartMs` and the window
//   would never roll over → permanent block. `rollIfExpired()` resets the
//   window to the current `Date.now()` whenever `now < windowStartMs`.
//
// Refs: phase 18 LAUNCH-14

const STORAGE_KEY = 'sentry-rate-limit-state-v1'
const WINDOW_MS = 3_600_000 // 1 hour
const CAP = 100

type State = { count: number; windowStartMs: number }

function readState(): State {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { count: 0, windowStartMs: Date.now() }
    const parsed: unknown = JSON.parse(raw)
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as { count?: unknown }).count !== 'number' ||
      typeof (parsed as { windowStartMs?: unknown }).windowStartMs !== 'number'
    ) {
      return { count: 0, windowStartMs: Date.now() }
    }
    return parsed as State
  } catch {
    // Corrupted JSON or LocalStorage disabled — fallback to empty state.
    return { count: 0, windowStartMs: Date.now() }
  }
}

function writeState(state: State): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // LocalStorage full or disabled — fail silent. Rate limit becomes
    // best-effort (still respects in-memory state for the session).
  }
}

function rollIfExpired(state: State, now: number): State {
  // Forward window expiry (normal rollover).
  if (now - state.windowStartMs > WINDOW_MS) {
    return { count: 0, windowStartMs: now }
  }
  // Clock-skew guard (Gemini Gate 1 2026-05-07) :
  // If Date.now() goes backward (NTP correction, timezone change),
  // reset the window to current time so we don't get permanently blocked.
  if (now < state.windowStartMs) {
    return { count: 0, windowStartMs: now }
  }
  return state
}

export function shouldCaptureEvent(): boolean {
  const now = Date.now()
  const state = rollIfExpired(readState(), now)
  writeState(state)
  return state.count < CAP
}

export function recordCapture(): void {
  const now = Date.now()
  let state = rollIfExpired(readState(), now)
  state = { ...state, count: Math.min(CAP, state.count + 1) }
  writeState(state)
}

export function resetForTests(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
